import {
  WuiBox,
  WuiButton,
  WuiCloseButton,
  WuiCodeSnippet,
  WuiDivider,
  WuiLayout,
  WuiProgressBar,
  WuiStatus,
} from '@jwplayer/wui-react';

import { Link } from '/module/routing/link';

import css from './style.module.scss';
import type { ButtonItem } from './types';
import { REMOVE_BTN_TYPE } from './util';

export const UploadTile = ({
  id,
  siteId,
  type,
  label,
  fileName,
  loading = false,
  progress,
  size,
  failed = false,
  buttonItems,
  removeBtn,
  image,
}: {
  label?: string;
  id?: string;
  siteId?: string;
  type?: string;
  size?: string;
  fileName?: string;
  loading?: boolean;
  progress?: number;
  failed?: boolean;
  buttonItems?: ButtonItem[];
  removeBtn?: { type: string; onClick: () => void };
  image?: {
    src: string;
    error?: Error;
    onError?: () => void;
  };
}) => {
  const { type: removeBtnType, onClick: onRemove } = removeBtn || {};
  const { src: imageSrc, error, onError } = image || {};

  const displayImagePreview = imageSrc && !error && !loading;

  return (
    <WuiBox className={`${css.container} ${failed ? css.failed : ''}`}>
      <WuiBox orientation="horizontal" align="center" justify="space-between">
        <WuiBox orientation="horizontal" gutter="xs" className={css.textContainer}>
          {id && siteId ? (
            <WuiBox className={css.link}>
              <Link
                to={{
                  pathname: 'mediaDetail',
                  params: { mediaId: id, siteId },
                }}
              >
                <span>{label}</span>
              </Link>
            </WuiBox>
          ) : (
            label && <span className={css.label}>{label}</span>
          )}

          {size && <p className={css.size}>{size}</p>}

          {fileName && <p className={css.fileName}>{fileName}</p>}
        </WuiBox>

        <WuiBox orientation="horizontal" align="center" gutter="sm">
          {loading && (
            <WuiProgressBar
              className={css.progressBar}
              data-test="upload-progress"
              type="linear"
              progressValue={progress}
              indeterminate={!progress}
            />
          )}

          {failed && <WuiStatus intent="error" type="icon" text="Failed" />}

          <WuiLayout type="button-group" variant="button-group-content-container">
            {buttonItems?.map((button: ButtonItem, idx) => {
              const { label, onClick } = button;
              return <WuiButton data-test={button.label} label={label} type="tertiary" onClick={onClick} key={idx} />;
            })}
          </WuiLayout>

          {!loading && id && (
            <WuiBox className={css.codeSnippet}>
              <p>
                {type} ID: <WuiCodeSnippet type="inline" code={id} />
              </p>
            </WuiBox>
          )}

          {removeBtn && (
            <>
              <WuiDivider type="vertical" />

              {removeBtnType === REMOVE_BTN_TYPE.remove && (
                <WuiBox orientation="horizontal" align="center">
                  <WuiCloseButton data-test="remove-file" size="md" tooltip="Remove" onClick={onRemove} />
                </WuiBox>
              )}

              {removeBtnType === REMOVE_BTN_TYPE.delete && (
                <WuiButton
                  data-test="delete"
                  type="icon"
                  intent="error"
                  tooltip="Delete"
                  icon="trash"
                  onClick={onRemove}
                />
              )}
            </>
          )}
        </WuiBox>
      </WuiBox>

      {displayImagePreview && (
        <div className={css.previewPane}>
          <img src={imageSrc} data-test="show-image-upload-preview-pane" onError={onError} />
        </div>
      )}
    </WuiBox>
  );
};
